import React, { useRef, useEffect, useState } from 'react';
import { Box, SpaceBetween, PromptInput, Link, Button, FormField, Select } from '@amzn/awsui-components-console';
import MessageList from './MessageList';

export default function ChatWindow({
    messages,
    onSendMessage,
    onNewConversation,
    isLoading,
    selectedAssistant,
    onAssistantChange,
}) {
    const [prompt, setPrompt] = useState('');

    const messagesContainerRef = useRef(null);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            container.scrollTop = container.scrollHeight;
        }
    }, [messages]);

    const handlePromptSend = () => {
        if (prompt.trim() && !isLoading && prompt.length <= 1000) {
            onSendMessage(prompt);
            setPrompt('');
            // Note: Auto-focus removed due to ref limitations with AWS UI components
        }
    };

    return (
        <div className="chatbot-window">
            <SpaceBetween size="l">
                {/* Messages Container */}
                <div
                    ref={messagesContainerRef}
                    className="messages-container"
                    style={{
                        height: '400px',
                        overflowY: 'auto',
                        padding: '16px',
                        backgroundColor: '#fafafa',
                        borderRadius: '8px',
                        border: '1px solid #e9ebed',
                    }}
                >
                    <MessageList messages={messages} isLoading={isLoading} />
                </div>

                {/* Input Area */}
                <div className="chat-input-area">
                    <form onSubmit={(e) => e.preventDefault()}>
                        <SpaceBetween size="xs">
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {/* Assistant Selection - Inline */}
                                <div
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}
                                >
                                    <Select
                                        selectedOption={selectedAssistant}
                                        onChange={({ detail }) => onAssistantChange(detail.selectedOption)}
                                        options={[
                                            {
                                                label: 'Default',
                                                options: [{ label: 'Default Assistant', value: 'default' }],
                                            },
                                            {
                                                label: 'Custom Agents',
                                                options: [
                                                    { label: 'Device Recovery Assistant', value: 'device-recovery' },
                                                ],
                                            },
                                        ]}
                                        disabled={isLoading}
                                        placeholder="Choose an assistant"
                                        expandToViewport
                                    />
                                </div>
                                <div style={{ flexGrow: 1 }}>
                                    <FormField
                                        stretch={true}
                                        errorText={
                                            prompt.length > 1000 &&
                                            'The question has too many characters. Please keep it under 1000 characters.'
                                        }
                                    >
                                        <PromptInput
                                            value={prompt}
                                            onChange={({ detail }) => setPrompt(detail.value)}
                                            onAction={handlePromptSend}
                                            placeholder={
                                                selectedAssistant?.value === 'device-recovery'
                                                    ? 'e.g. recover G001MG041092021E...'
                                                    : 'Ask me about Device Farm... (maximum input length is 1000)'
                                            }
                                            actionButtonIconName="send"
                                            actionButtonAriaLabel={isLoading ? 'Sending message...' : 'Send message'}
                                            ariaLabel="Chat input"
                                            autoFocus
                                            disabled={isLoading}
                                        />
                                    </FormField>
                                </div>
                                <Button
                                    variant="icon"
                                    iconName="refresh"
                                    onClick={onNewConversation}
                                    disabled={isLoading}
                                    ariaLabel="Start new conversation"
                                />
                            </div>

                            {/* Footer with disclaimer */}
                            <Box color="text-body-secondary" fontSize="body-s" textAlign="center">
                                {selectedAssistant?.value === 'device-recovery' ? (
                                    <>
                                        Device Recovery Assistant powered by AWS Bedrock Agent. Enter commands like
                                        "recover [DEVICE_ID]" to initiate device recovery.
                                    </>
                                ) : (
                                    <>
                                        This is a chatbot. Responses would be powered by AWS Bedrock with Device Farm
                                        documentation.{' '}
                                        <Link
                                            href="https://w.amazon.com/bin/view/DS2DeviceFarm/"
                                            external
                                            variant="primary"
                                            fontSize="inherit"
                                        >
                                            View Device Farm Documentation
                                        </Link>
                                    </>
                                )}
                            </Box>
                        </SpaceBetween>
                    </form>
                </div>
            </SpaceBetween>
        </div>
    );
}
